// Minimal, dependency-free IndexedDB cache for the bulk-item-update item list.
// Caches one page of `search_menu_items` results per key. Best-effort only:
// every operation swallows its own errors and resolves to a safe fallback
// (null / no-op) so a broken/unavailable IndexedDB (private browsing, quota,
// disabled) degrades to "treat as a cache miss" instead of breaking the page.

const DB_NAME = 'kravein-admin-cache';
const DB_VERSION = 1;
const STORE_NAME = 'bulk_item_update_menu_items';

let dbPromise = null;

function openCacheDB() {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB is not available'));
  }
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    }).catch(err => {
      dbPromise = null;
      return Promise.reject(err);
    });
  }
  return dbPromise;
}

const pageKey = ({ userId, placeName, saleType }, pageIndex) =>
  `${userId}::${placeName}::${saleType}::page${pageIndex}`;

export async function setCachedPage(params, pageIndex, items) {
  try {
    const db = await openCacheDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(
        { items, cachedAt: Date.now() },
        pageKey(params, pageIndex),
      );
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // best-effort — a failed cache write shouldn't affect the page
  }
}

export async function getCachedPage(params, pageIndex) {
  try {
    const db = await openCacheDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(pageKey(params, pageIndex));
      req.onsuccess = () => resolve(req.result ? req.result.items : null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}
